/**
 * Shared Arabic vocabulary for «حالة التشغيل» — the unit's operational status
 * (tasks/unit-operational-status.md).
 *
 * The backend answers in CODES only (`GET /api/v1/unit-status/:propertyId`); every surface that shows
 * the status — the unit page, the properties list, the inbox, «المتابعة» — renders them through THIS
 * file, so two screens can never word the same unit differently.
 *
 * ⚠️ ARABIC-ONLY, deliberately — same reasoning as `config/relocationLabels.js`: these surfaces
 * hardcode their Arabic, and a bilingual key set would imply a translated surface that does not exist.
 */

/** Staff view — the five statuses, in the resolver's precedence order. */
export const UNIT_STATUS_LABELS = {
  OCCUPIED: 'مشغولة',
  STOPPED: 'موقوفة',
  AWAITING_CLEANING: 'بانتظار النظافة',
  UNKNOWN: 'غير معروف',
  READY: 'جاهزة',
};

// The owner's four words (مشغولة / موقوفة / قيد التجهيز / متاحة) live in the locales, ownerApp.unitStatus — one home
// (owner app 8c). This file is the staff view.

/** Why — one sentence per reasonCode. Surfaces key off the code, never off this text. */
export const UNIT_STATUS_REASONS = {
  GUEST_IN_STAY: 'ضيف داخل الوحدة',
  RELOCATED_GUEST: 'ضيف منقول من وحدة أخرى داخل الوحدة',
  UNIT_STOPPED: 'أوقفها فريق العمليات',
  UNIT_STOPPED_REVIEW_DUE: 'بحاجة لمراجعة — تجاوز الإيقاف موعد المراجعة',
  CALENDAR_CLOSED_NO_REASON: 'إغلاق تقويم بدون سبب مسجل',
  TURNOVER_CLEAN_PENDING: 'غادر الضيف ولم يكتمل التنظيف',
  TURNOVER_CLEAN_SCHEDULED_LATER: 'غادر الضيف والتنظيف مجدول لاحقاً',
  NO_TURNOVER_CLEAN: 'غادر الضيف ولا توجد مهمة تنظيف مسجلة',
  ARRIVAL_TIME_UNKNOWN: 'تعذّر تحديد موعد وصول الضيف',
  DEPARTURE_TIME_UNKNOWN: 'تعذّر تحديد موعد مغادرة الضيف',
  LOOKUP_FAILED: 'تعذّر التحقق من حالة الوحدة',
  CLEAN: 'نظيفة ولا يوجد ما يمنع الاستقبال',
};

/**
 * D10 — based on the schedule, not observed. Confirmed evidence (a door entry, a checkout record)
 * carries no tag. Founder, 2026-09-24: «حسب الموعد» replaces «يُفترض», which read as doubt about the
 * whole status rather than saying what it rests on.
 */
export const UNIT_STATUS_PRESUMED = 'حسب الموعد';
export const UNIT_STATUS_PRESUMED_HINT = 'لم يُسجَّل دخول أو خروج فعلي للضيف — الحالة مبنية على الموعد';

export const UNIT_STATUS_NOTES = {
  REFRESH_CLEAN_DUE: 'تحتاج تنظيف تنشيطي',
  STOP_PENDING_DEPARTURE: 'الإيقاف يبدأ بعد مغادرة الضيف الحالي',
  CALENDAR_NO_DATA: 'لا توجد بيانات تقويم لهذه الليلة',
  openMaintenance: (n) => (n === 1 ? 'بلاغ صيانة مفتوح' : `${n} بلاغات صيانة مفتوحة`),
  fromLastClean: 'أُبلغ عنه في آخر تنظيف',
  cleaningLate: 'تأخر التنظيف — موعد وصول الضيف التالي مضى',
  cleaningAtRisk: 'التنظيف قد لا يكتمل قبل وصول الضيف التالي',
  cleaningTask: 'مهمة التنظيف',
};

/** «أوقفها …» — who stopped the unit, when the backend knows (staff view only). */
export const stoppedBy = (name) => `أوقفها ${name}`;

/** «إيقاف الوحدة» reasons — keys match `STOP_REASONS` in be/models/UnitStop.js. */
export const STOP_REASON_LABELS = {
  maintenance: 'صيانة / عطل',
  deep_cleaning: 'تنظيف عميق',
  owner_use: 'استخدام المالك',
  inspection: 'فحص',
  other: 'أخرى',
};

/** What happened when the stop sale was sent to the booking platforms. */
export const STOP_CHANNEL_LABELS = {
  pending: 'جارٍ إيقاف البيع على المنصات…',
  failed: 'لم يصل إيقاف البيع إلى المنصات — تحقق منها يدوياً',
};

export const UNIT_STOP_UI = {
  title: 'حالة التشغيل',
  stop: 'إيقاف الوحدة',
  edit: 'تعديل الإيقاف',
  resume: 'استئناف',
  stopModalTitle: 'إيقاف الوحدة',
  editModalTitle: 'تعديل الإيقاف',
  resumeModalTitle: 'استئناف الوحدة',
  reason: 'سبب الإيقاف',
  note: 'ملاحظة',
  notePlaceholder: 'ما المشكلة، وما المطلوب لإعادة تشغيل الوحدة؟',
  noteRequired: 'مطلوبة عند اختيار «أخرى»',
  reviewAt: 'موعد المراجعة',
  reviewHelp: 'إذا لم تُستأنف الوحدة حتى هذا الموعد تظهر «بحاجة لمراجعة» في «المتابعة».',
  closeOnChannels: 'إغلاق الوحدة على منصات الحجز',
  closedThrough: 'إغلاق حتى تاريخ',
  closedThroughHelp: 'آخر ليلة يُوقف فيها البيع. يمكن تمديده لاحقاً، والاستئناف يعيد فتح الوحدة مبكراً.',
  confirmStopLead: 'سيتم إيقاف البيع على جميع منصات الحجز',
  confirmStopRange: (from, to) => (from === to ? `لليلة ${from}.` : `من ليلة ${from} حتى ليلة ${to}.`),
  confirmExtendLead: 'سيتم تمديد إيقاف البيع على جميع منصات الحجز',
  confirmExtendTo: (to) => `حتى ليلة ${to}.`,
  confirmStopKeep: 'الحجوزات الموجودة لا تتأثر.',
  confirmStop: 'تأكيد الإيقاف',
  saveEdit: 'حفظ التعديل',
  cancel: 'إلغاء',
  back: 'رجوع',
  resumeBody: 'ستعود الوحدة للاستقبال، ويُرفع إيقاف البيع الذي وضعه هذا الإيقاف فقط. أي إيقاف بيع وضعته يدوياً يبقى كما هو.',
  resumeConfirm: 'تأكيد الاستئناف',
  stoppedSince: 'منذ',
  reviewOn: 'المراجعة',
  closedUntil: 'مغلقة على المنصات حتى ليلة',
  loadFailed: 'تعذّر تحميل حالة التشغيل',
  // The browser's own validation text follows the browser's language — ours is always Arabic.
  fieldRequired: 'هذا الحقل مطلوب',
  closedThroughTooEarly: 'لا يمكن اختيار ليلة قبل هذا التاريخ — استخدم «استئناف» لإعادة فتح الوحدة',
  saveFailed: 'تعذّر حفظ الإيقاف',
};
